/**
 * Reading an uploaded plano with a vision model ("Detectar paredes"), so a tour without
 * LiDAR still gets a Maqueta: walls, doors, windows and room outlines.
 *
 * The model answers in PERMILLE of the image (integers 0..1000, x from the left edge,
 * y from the top), each axis relative to its own side — the same shape whatever size the
 * server resizes the upload to, and easier for a model than fractions. That is an API
 * shape only: `readingToPlan` turns it into metres with the scale the room pins already
 * gave (planPlacement), so tour.json keeps the metres-only contract.
 *
 * The prompt and JSON schema live here, next to the parser, so the readPlan callable and
 * the offline scorer (tools/planread) can never drift apart.
 */
import { z } from 'zod';
/** Permille units per image side. */
export const PLAN_READING_UNITS = 1000;
/** Caps on what one plano can contain; anything above is a misread, not a flat. */
export const PLAN_READING_LIMITS = { walls: 400, openings: 200, rooms: 40, outline: 64, name: 40 };
/** Walls shorter than this (permille) are noise: hatching, a door jamb, a text stroke. */
const MIN_WALL = 6;
/** A wall within this angle of horizontal/vertical is snapped onto the axis. */
const AXIS_SNAP_RAD = (2.5 * Math.PI) / 180;
/** Openings further than this (permille) from every wall are dropped. */
const MAX_OPENING_OFFSET = 18;
const Num = z.number().finite();
const Segment = { x1: Num, y1: Num, x2: Num, y2: Num };
export const PlanReading = z.object({
    /** The image isn't a floor plan (a photo, a façade, a blank page). */
    notAPlan: z.boolean().default(false),
    walls: z.array(z.object(Segment)).default([]),
    openings: z.array(z.object({ kind: z.enum(['door', 'window']), ...Segment })).default([]),
    rooms: z
        .array(z.object({ name: z.string().default(''), outline: z.array(z.object({ x: Num, y: Num })) }))
        .default([]),
});
const clamp = (v) => Math.min(PLAN_READING_UNITS, Math.max(0, v));
const len = (s) => Math.hypot(s.b.x - s.a.x, s.b.y - s.a.y);
/** Snap a nearly-axis-aligned segment onto the axis, around its midpoint. */
function snapAxis(s) {
    const angle = Math.atan2(s.b.y - s.a.y, s.b.x - s.a.x);
    const fold = Math.abs(((angle % Math.PI) + Math.PI) % Math.PI);
    if (fold < AXIS_SNAP_RAD || Math.PI - fold < AXIS_SNAP_RAD) {
        const y = (s.a.y + s.b.y) / 2;
        return { a: { x: s.a.x, y }, b: { x: s.b.x, y } };
    }
    if (Math.abs(fold - Math.PI / 2) < AXIS_SNAP_RAD) {
        const x = (s.a.x + s.b.x) / 2;
        return { a: { x, y: s.a.y }, b: { x, y: s.b.y } };
    }
    return s;
}
/** Closest point on segment `s` to `p`, and the distance to it. */
function project(s, p) {
    const dx = s.b.x - s.a.x;
    const dy = s.b.y - s.a.y;
    const l2 = dx * dx + dy * dy;
    const t = l2 === 0 ? 0 : Math.min(1, Math.max(0, ((p.x - s.a.x) * dx + (p.y - s.a.y) * dy) / l2));
    const point = { x: s.a.x + t * dx, y: s.a.y + t * dy };
    return { point, dist: Math.hypot(p.x - point.x, p.y - point.y) };
}
/**
 * Validate and tidy a model reply: clamp to the image, snap near-axis walls, drop
 * stubs, move each opening onto its nearest wall (or drop it), cap every list. Returns
 * null when the reply isn't a reading at all.
 */
export function cleanReading(raw) {
    const parsed = PlanReading.safeParse(raw);
    if (!parsed.success)
        return null;
    const r = parsed.data;
    const L = PLAN_READING_LIMITS;
    const seg = (v) => ({
        a: { x: clamp(v.x1), y: clamp(v.y1) },
        b: { x: clamp(v.x2), y: clamp(v.y2) },
    });
    const walls = r.walls
        .map((w) => snapAxis(seg(w)))
        .filter((w) => len(w) >= MIN_WALL)
        .slice(0, L.walls);
    const openings = [];
    for (const o of r.openings) {
        if (openings.length >= L.openings || walls.length === 0)
            break;
        const s = seg(o);
        const mid = { x: (s.a.x + s.b.x) / 2, y: (s.a.y + s.b.y) / 2 };
        let best = null;
        for (const wall of walls) {
            const { dist } = project(wall, mid);
            if (!best || dist < best.dist)
                best = { wall, dist };
        }
        if (!best || best.dist > MAX_OPENING_OFFSET)
            continue;
        const a = project(best.wall, s.a).point;
        const b = project(best.wall, s.b).point;
        if (Math.hypot(b.x - a.x, b.y - a.y) < MIN_WALL / 2)
            continue;
        openings.push({ kind: o.kind, a, b });
    }
    const rooms = r.rooms
        .map((room) => ({
        name: room.name.trim().slice(0, L.name),
        outline: room.outline.slice(0, L.outline).map((p) => ({ x: clamp(p.x), y: clamp(p.y) })),
    }))
        .filter((room) => room.outline.length >= 3)
        .slice(0, L.rooms);
    return { notAPlan: r.notAPlan, walls, openings, rooms };
}
/** Permille → tour.json metres (origin at the image's top-left, y down, as planPlacement). */
export function readingToPlan(reading, scale) {
    const m = (p) => ({
        x: (p.x / PLAN_READING_UNITS) * scale.widthPx * scale.metersPerPixel,
        y: (p.y / PLAN_READING_UNITS) * scale.heightPx * scale.metersPerPixel,
    });
    return {
        walls: reading.walls.map((w) => ({ a: m(w.a), b: m(w.b) })),
        openings: reading.openings.map((o) => ({ kind: o.kind, a: m(o.a), b: m(o.b) })),
        rooms: reading.rooms.map((r) => ({ name: r.name, outline: r.outline.map(m) })),
    };
}
/** The serialisable shape the dashboard sends back after the owner reviewed it. */
export function readingPayload(reading) {
    const flat = (s) => ({ x1: s.a.x, y1: s.a.y, x2: s.b.x, y2: s.b.y });
    return {
        notAPlan: reading.notAPlan,
        walls: reading.walls.map(flat),
        openings: reading.openings.map((o) => ({ kind: o.kind, ...flat(o) })),
        rooms: reading.rooms,
    };
}
/**
 * The plan-reading instructions, sent as the (cached) system prompt; the image and
 * PLAN_READING_USER form the user turn. functions readPlan and tools/planread/score.mjs
 * both send exactly this, so a score is a score of production.
 */
export const PLAN_READING_PROMPT = `You are reading an architectural floor plan of a flat or house so it can be rebuilt as a simple 3D model. Trace it precisely.

Coordinates: integers from 0 to 1000 on each axis, relative to the WHOLE image. x = 0 is the left edge and 1000 the right edge; y = 0 is the top edge and 1000 the bottom edge. The axes are independent, so a non-square image still spans 0..1000 on both.

walls: every wall as a straight segment along its CENTRE LINE, exterior and interior, including balcony and terrace railings. Split walls at corners and T-junctions so each segment is straight. Run a wall straight through its doors and windows; do not break it at openings. Make connected walls meet exactly at shared endpoints. Ignore furniture, fixtures, dimension lines, text, hatching, stairs, north arrows, title blocks and anything outside the dwelling.

openings: each door and window as the span it occupies along its wall (the two ends of the opening on the wall's centre line). For a door, use the gap in the wall, not the swing arc.

rooms: one entry per room or space that has a name on the plan, with the name exactly as written (for example "Salón-comedor", "Dormitorio 1", "Baño", "Terraza"). Spaces without a written name (a hall, a closet) get a plain name in the language of the plan's labels, such as "Pasillo" or "Armario" on a Spanish plan, "Hallway" or "Closet" on an English one; Spanish if the plan has no labels. The outline is the room's floor polygon following the inside of its walls, corners in order.

If the image contains several floor plans, read only the largest one. If it is not a floor plan at all (a photograph, a façade, a document), set notAPlan to true and leave every list empty.`;
export const PLAN_READING_USER = 'Trace this floor plan.';
const SEGMENT_PROPS = {
    x1: { type: 'integer' },
    y1: { type: 'integer' },
    x2: { type: 'integer' },
    y2: { type: 'integer' },
};
/** JSON schema for structured outputs (output_config.format). */
export const PLAN_READING_JSON_SCHEMA = {
    type: 'object',
    additionalProperties: false,
    required: ['notAPlan', 'walls', 'openings', 'rooms'],
    properties: {
        notAPlan: { type: 'boolean' },
        walls: {
            type: 'array',
            items: { type: 'object', additionalProperties: false, required: ['x1', 'y1', 'x2', 'y2'], properties: SEGMENT_PROPS },
        },
        openings: {
            type: 'array',
            items: {
                type: 'object',
                additionalProperties: false,
                required: ['kind', 'x1', 'y1', 'x2', 'y2'],
                properties: { kind: { type: 'string', enum: ['door', 'window'] }, ...SEGMENT_PROPS },
            },
        },
        rooms: {
            type: 'array',
            items: {
                type: 'object',
                additionalProperties: false,
                required: ['name', 'outline'],
                properties: {
                    name: { type: 'string' },
                    outline: {
                        type: 'array',
                        items: {
                            type: 'object',
                            additionalProperties: false,
                            required: ['x', 'y'],
                            properties: { x: { type: 'integer' }, y: { type: 'integer' } },
                        },
                    },
                },
            },
        },
    },
};
//# sourceMappingURL=planReading.js.map